// frontend/src/lib/reporteNovedadesPdf.ts
// Hojas HTML del PDF "Novedades y Licencias por período" (se rasterizan con
// htmlAPdf, mismo formato A4 que la Nómina). Datos: JSON de
// GET /api/reportes/novedades.

import type { ReporteNovedades } from './api';
import { CODIGOS_NOVEDAD, NOMBRE_CODIGO, fechaCorta } from './novedades';
import { escapeHtml, footerSelloFirmaHtml } from './reportePdf';

// Filas por hoja A4 (igual que la Nómina). Los encabezados de tabla y el bloque
// de resumen "cuestan" filas para no desbordar la hoja.
export const FILAS_POR_PAGINA = 45;
const COSTO_TITULO_TABLA = 3;
const COSTO_RESUMEN = 9;

interface Bloque {
  costo: number;
  // Tabla a la que pertenece la fila (para repetir el thead al cambiar de hoja)
  tabla?: 'novedades' | 'licencias';
  html: string;
}

function nombreCadete(c: { apellido?: string | null; nombres?: string | null } | undefined): string {
  if (!c) return 'CADETE NO ENCONTRADO';
  return `${c.apellido || ''} ${c.nombres || ''}`.trim().toUpperCase();
}

const THEAD_NOVEDADES = `
        <thead>
          <tr>
            <th style="width: 9%;">Fecha</th>
            <th style="width: 27%;">Apellido y Nombres</th>
            <th style="width: 10%;">DNI N°</th>
            <th style="width: 7%;">Código</th>
            <th style="width: 39%;">Motivo</th>
            <th style="width: 8%;">Hora</th>
          </tr>
        </thead>`;

const THEAD_LICENCIAS = `
        <thead>
          <tr>
            <th style="width: 25%;">Apellido y Nombres</th>
            <th style="width: 10%;">DNI N°</th>
            <th style="width: 7%;">Código</th>
            <th style="width: 16%;">Tipo</th>
            <th style="width: 9%;">Desde</th>
            <th style="width: 9%;">Hasta</th>
            <th style="width: 24%;">Detalle</th>
          </tr>
        </thead>`;

function tituloSeccion(texto: string): string {
  return `<h2 style="font-size: 9px; font-weight: 800; margin: 6px 0 4px 0; letter-spacing: 0.4px; text-transform: uppercase;">${escapeHtml(texto)}</h2>`;
}

/**
 * Construye las hojas (`<div class="report-page">`) del reporte. Título,
 * tabla de novedades (Fecha, Apellido y Nombres, DNI, Código, Motivo, Hora),
 * tabla de licencias, bloque resumen y sello/firma en la última hoja.
 */
export function construirPaginasNovedades(reporte: ReporteNovedades, selloBase64: string | null): string[] {
  const cadetesPorId = new Map(reporte.cadetes.map((c) => [c.id, c]));
  const titulo = `NOVEDADES Y LICENCIAS — ${reporte.compania.nombre.toUpperCase()} — ${fechaCorta(reporte.desde)} AL ${fechaCorta(reporte.hasta)}`;

  // ── Secuencia de bloques ────────────────────────────────────────────────
  const bloques: Bloque[] = [];

  bloques.push({ costo: COSTO_TITULO_TABLA, html: tituloSeccion(`Novedades (${reporte.novedades.length})`) });
  if (reporte.novedades.length === 0) {
    bloques.push({ costo: 1, html: `<p style="font-size: 8px; font-style: italic; margin: 0 0 8px 0;">Sin novedades (ausencias ni tardanzas) en el período.</p>` });
  }
  const novedadesOrdenadas = reporte.novedades.slice().sort((a, b) => {
    if (a.fecha !== b.fecha) return a.fecha.localeCompare(b.fecha);
    return nombreCadete(cadetesPorId.get(a.cadete_id)).localeCompare(nombreCadete(cadetesPorId.get(b.cadete_id)), 'es', { sensitivity: 'base' });
  });
  for (const n of novedadesOrdenadas) {
    const c = cadetesPorId.get(n.cadete_id);
    const codigo = n.codigo || (n.estado === 'tarde' ? 'TARDE' : '-');
    const hora = n.hora_local ? String(n.hora_local).substring(0, 5) : '-';
    bloques.push({
      costo: 1,
      tabla: 'novedades',
      html: `
          <tr>
            <td class="center">${escapeHtml(fechaCorta(n.fecha))}</td>
            <td class="bold font-inter">${escapeHtml(nombreCadete(c))}</td>
            <td class="center">${escapeHtml(c?.dni || '')}</td>
            <td class="center bold">${escapeHtml(codigo)}</td>
            <td>${escapeHtml(n.motivo || (n.estado === 'tarde' ? 'Tardanza' : ''))}</td>
            <td class="center">${escapeHtml(hora)}</td>
          </tr>`,
    });
  }

  bloques.push({ costo: COSTO_TITULO_TABLA, html: tituloSeccion(`Licencias (${reporte.licencias.length})`) });
  if (reporte.licencias.length === 0) {
    bloques.push({ costo: 1, html: `<p style="font-size: 8px; font-style: italic; margin: 0 0 8px 0;">Sin licencias solapadas con el período.</p>` });
  }
  const licenciasOrdenadas = reporte.licencias.slice().sort((a, b) => {
    const na = nombreCadete(cadetesPorId.get(a.cadete_id));
    const nb = nombreCadete(cadetesPorId.get(b.cadete_id));
    if (na !== nb) return na.localeCompare(nb, 'es', { sensitivity: 'base' });
    return a.fecha_inicio.localeCompare(b.fecha_inicio);
  });
  for (const l of licenciasOrdenadas) {
    const c = cadetesPorId.get(l.cadete_id);
    bloques.push({
      costo: 1,
      tabla: 'licencias',
      html: `
          <tr>
            <td class="bold font-inter">${escapeHtml(nombreCadete(c))}</td>
            <td class="center">${escapeHtml(c?.dni || '')}</td>
            <td class="center bold">${escapeHtml(l.tipo_codigo || '')}</td>
            <td>${escapeHtml(l.tipo_nombre || '')}</td>
            <td class="center">${escapeHtml(fechaCorta(l.fecha_inicio))}</td>
            <td class="center">${escapeHtml(l.fecha_fin ? fechaCorta(l.fecha_fin) : 'Hasta alta')}</td>
            <td>${escapeHtml(l.motivo_detalle || '')}</td>
          </tr>`,
    });
  }

  // Resumen
  const r = reporte.resumen;
  const celdasCodigo = CODIGOS_NOVEDAD.map((cod) => `
            <td class="center" style="width: 12%;">
              <div style="font-size: 11px; font-weight: 800;">${escapeHtml(r.por_codigo?.[cod] ?? 0)}</div>
              <div style="font-size: 6.5px; font-weight: 700;">${escapeHtml(cod)}</div>
              <div style="font-size: 6px; color: #333;">${escapeHtml(NOMBRE_CODIGO[cod])}</div>
            </td>`).join('');
  bloques.push({
    costo: COSTO_RESUMEN,
    html: `
        ${tituloSeccion('Resumen del período')}
        <table>
          <tbody>
            <tr>${celdasCodigo}
              <td class="center" style="width: 14%;">
                <div style="font-size: 11px; font-weight: 800;">${escapeHtml(r.tardes ?? 0)}</div>
                <div style="font-size: 6.5px; font-weight: 700;">TARDANZAS</div>
              </td>
              <td class="center" style="width: 14%;">
                <div style="font-size: 11px; font-weight: 800;">${escapeHtml(r.licencias ?? 0)}</div>
                <div style="font-size: 6.5px; font-weight: 700;">LICENCIAS</div>
              </td>
            </tr>
          </tbody>
        </table>
        <p style="font-size: 7.5px; margin: 0 0 6px 0;">
          <strong>Total de novedades:</strong> ${escapeHtml(r.total_novedades ?? reporte.novedades.length)} ·
          <strong>Días del período:</strong> ${escapeHtml(r.dias ?? '')} ·
          <strong>Cadetes de la compañía:</strong> ${escapeHtml(reporte.cadetes.length)} ·
          <strong>Generado:</strong> ${escapeHtml(new Date(reporte.generado_en).toLocaleString('es-AR'))}
        </p>`,
  });

  // ── Paginación: FILAS_POR_PAGINA por hoja; el thead se repite al continuar ──
  const paginas: Bloque[][] = [];
  let actual: Bloque[] = [];
  let usado = 0;
  for (const b of bloques) {
    const costo = b.costo + (b.tabla && (actual.length === 0 || actual[actual.length - 1].tabla !== b.tabla) ? 2 : 0);
    if (usado + costo > FILAS_POR_PAGINA && actual.length > 0) {
      paginas.push(actual);
      actual = [];
      usado = 0;
    }
    actual.push(b);
    usado += costo;
  }
  if (actual.length > 0) paginas.push(actual);

  const totalPaginas = paginas.length;
  return paginas.map((bloquesPagina, p) => {
    // Agrupar filas consecutivas de la misma tabla en un <table>
    let cuerpo = '';
    let tablaAbierta: Bloque['tabla'] | undefined;
    const cerrar = () => { if (tablaAbierta) { cuerpo += '</tbody></table>'; tablaAbierta = undefined; } };
    for (const b of bloquesPagina) {
      if (b.tabla) {
        if (tablaAbierta !== b.tabla) {
          cerrar();
          cuerpo += `<table>${b.tabla === 'novedades' ? THEAD_NOVEDADES : THEAD_LICENCIAS}<tbody>`;
          tablaAbierta = b.tabla;
        }
        cuerpo += b.html;
      } else {
        cerrar();
        cuerpo += b.html;
      }
    }
    cerrar();

    const isLast = p === totalPaginas - 1;
    return `
          <div class="report-page">
            <div class="header">
              <h1 style="font-size: 10px; font-weight: 800; margin: 0; letter-spacing: 0.4px; max-width: 520px;">${escapeHtml(titulo)}</h1>
              <div style="font-size: 9px; font-weight: 700; text-align: right;">
                PERÍODO: ${escapeHtml(fechaCorta(reporte.desde))} – ${escapeHtml(fechaCorta(reporte.hasta))}<br>
                <span style="font-size: 8px; font-weight: 600;">Hoja ${p + 1} de ${totalPaginas}</span>
              </div>
            </div>
            ${cuerpo}
            ${isLast ? footerSelloFirmaHtml(selloBase64) : ''}
          </div>
        `;
  });
}
